import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCrown, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { changePage } from "../../store/actions";
import { resolveVipState, tierStyle, fmtUsd, GRACE_ICON } from "../vip/vipTierUtils";

// Dashboard "VIP Tiers" section (PARTNER_VOLUME_DISCOUNT_PLAN.md §9.1).
// Fancy compact view: crown header, discount hero, milestone track.
// The full explainer lives on /vip-tiers.
const TEXT = "#e6eae7";
const MUTED = "#7c847e";

const PartnerTierCard = () => {
  const dispatch = useDispatch();
  const pd = useSelector((state) => state.userReducer.partnerDiscount);
  const vip = resolveVipState(pd);
  if (!vip) return null;

  const { tiers, discountPct, vol30, currentIdx, badgeIdx, inGrace, graceUntil, next, remaining, trackPct } = vip;
  const cur = badgeIdx >= 0 ? tierStyle(badgeIdx) : null;
  const accent = cur ? cur.color : "#3ad17e";

  return (
    <div
      className="panel"
      style={{
        marginTop: 16,
        position: "relative",
        overflow: "hidden",
        background:
          `radial-gradient(600px 180px at 12% -20%, ${accent}1f, transparent 60%),` +
          `radial-gradient(500px 220px at 95% 130%, ${accent}14, transparent 65%)`,
      }}
    >
      {/* Header */}
      <div className="pHd" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 17,
              color: accent,
              background: `${accent}1a`,
              border: `1px solid ${accent}40`,
              boxShadow: `0 0 18px ${accent}30`,
            }}
          >
            <FontAwesomeIcon icon={cur ? cur.icon : faCrown} />
          </span>
          <div>
            <div className="pTitle" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              VIP Tiers
              {cur && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: ".06em",
                    color: accent,
                    background: `${accent}1a`,
                    border: `1px solid ${accent}45`,
                    borderRadius: 999,
                    padding: "3px 10px",
                  }}
                >
                  {cur.name}
                </span>
              )}
            </div>
            <div className="pSub">Your volume unlocks lower InstaSwap fees</div>
          </div>
        </div>
        <Link
          to="/vip-tiers"
          onClick={() => dispatch(changePage("vip-tiers"))}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            fontSize: 12.5,
            fontWeight: 700,
            color: accent,
            textDecoration: "none",
          }}
        >
          How it works <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 11 }} />
        </Link>
      </div>

      {/* Hero row: discount + 30d volume + next goal */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 26, flexWrap: "wrap", margin: "2px 2px 20px" }}>
        <div>
          <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: "-0.02em", color: accent, lineHeight: 1 }}>
            {discountPct}%
            <span style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginLeft: 7 }}>fee discount</span>
          </div>
        </div>
        <div style={{ fontSize: 13, color: "#a7b2ab" }}>
          30-day volume <b style={{ color: TEXT, fontSize: 15, marginLeft: 4 }}>{fmtUsd(vol30)}</b>
        </div>
        {next ? (
          <div style={{ fontSize: 13, color: "#a7b2ab" }}>
            <b style={{ color: TEXT }}>{fmtUsd(remaining)}</b> {inGrace ? "to secure" : "to unlock"}{" "}
            <b style={{ color: tierStyle(currentIdx + 1).color }}>
              {tierStyle(currentIdx + 1).name} · {next.discountPct}% off
            </b>
          </div>
        ) : (
          tiers.length > 0 && (
            <div style={{ fontSize: 13, fontWeight: 700, color: accent }}>
              <FontAwesomeIcon icon={faCrown} style={{ marginRight: 6 }} />
              Highest tier unlocked
            </div>
          )
        )}
      </div>

      {inGrace && graceUntil && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12.5,
            color: "#e6c06a",
            background: "rgba(230,192,106,.08)",
            border: "1px solid rgba(230,192,106,.3)",
            borderRadius: 10,
            padding: "8px 12px",
            margin: "0 2px 18px",
          }}
        >
          <FontAwesomeIcon icon={GRACE_ICON} />
          {cur ? cur.name : "Your tier"} protected until {String(graceUntil).slice(0, 10)}
          {next ? <> — reach {fmtUsd(next.minVolumeUsd)} to keep it</> : null}
        </div>
      )}

      {/* Milestone track */}
      {tiers.length > 0 && (
        <div style={{ padding: "6px 2px 2px", overflowX: "auto" }}>
          <div style={{ position: "relative", height: 78, minWidth: Math.max(420, (tiers.length + 1) * 92) }}>
            {/* rail + fill */}
            <div
              style={{
                position: "absolute",
                left: 43,
                right: 43,
                top: 19,
                height: 6,
                borderRadius: 6,
                background: "rgba(255,255,255,.07)",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: 43,
                top: 19,
                height: 6,
                width: `calc((100% - 86px) * ${trackPct / 100})`,
                borderRadius: 6,
                background: `linear-gradient(90deg, #3ad17e, ${accent})`,
                boxShadow: `0 0 12px ${accent}55`,
                transition: "width .4s",
              }}
            />
            {/* milestone nodes: Start + one per tier */}
            {[null, ...tiers].map((t, i) => {
              const leftFrac = tiers.length > 0 ? i / tiers.length : 0;
              const achieved = i === 0 || currentIdx >= i - 1;
              const isCurrent = i - 1 === currentIdx;
              const st = i === 0 ? null : tierStyle(i - 1);
              const nodeColor = i === 0 ? "#3ad17e" : st.color;
              return (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    left: `calc(43px + (100% - 86px) * ${leftFrac})`,
                    top: 0,
                    transform: "translateX(-50%)",
                    textAlign: "center",
                    width: 86,
                  }}
                >
                  <div
                    style={{
                      width: isCurrent ? 26 : 18,
                      height: isCurrent ? 26 : 18,
                      margin: `${isCurrent ? 9 : 13}px auto 0`,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: isCurrent ? 11 : 8,
                      color: achieved ? "#0d1512" : "#5a635d",
                      background: achieved ? nodeColor : "#1a201d",
                      border: achieved ? `2px solid ${nodeColor}` : "2px solid rgba(255,255,255,.14)",
                      boxShadow: isCurrent ? `0 0 14px ${nodeColor}80` : "none",
                      transition: "all .3s",
                    }}
                  >
                    {i > 0 && <FontAwesomeIcon icon={st.icon} />}
                  </div>
                  <div
                    style={{
                      marginTop: 7,
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: achieved ? (i === 0 ? "#a7b2ab" : nodeColor) : "#5a635d",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {i === 0 ? "Start" : `${t.discountPct}% off`}
                  </div>
                  <div style={{ fontSize: 10, color: MUTED, whiteSpace: "nowrap" }}>
                    {i === 0 ? "" : fmtUsd(t.minVolumeUsd)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerTierCard;
